import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Table } from '~/components/Table';
import { usePagination } from '~/hooks/usePagination';
import { useUserList } from '~/modules/settings/api/queries/userList';
import { FilterDrawer } from '~/modules/settings/components/FilterDrawer';
import { useDefaultSort } from '~/modules/settings/hooks/useDefaultSort';
import { useFilters } from '~/modules/settings/hooks/useFilters';
import { useSearch } from '~/modules/settings/hooks/useSearch';
import type { UserListItem } from '~settings/api/lib/schemas';
import { HeaderSmall } from '~settings/components/HeaderSmall';
import { ListControls } from '~settings/components/ListControls';
import { SETTINGS_ROUTES } from '~settings/config/routes';

import { LabelUserFormFilter } from '../LabelUserFilterForm/LabelUserFilterForm';
import { useUsersColumns } from './hooks/useUsersColumns';

const userEmptyArray: UserListItem[] = [];

interface UsersProps {
  isSublabel: boolean;
  labelId?: string;
  total: number;
}

const DEFAULT_FILTER = { isActive: 'true' };

export function Users({ isSublabel, labelId, total }: UsersProps) {
  const { t } = useTranslation('settings');
  const columns = useUsersColumns(labelId);
  const navigate = useNavigate();

  const { searchTerm } = useSearch();
  const { page, setPageNumber, perPage, skip, take } = usePagination();
  const [searchParams, setSearchParams] = useSearchParams();

  const { filterData, drawerOpen, closeDrawer, openDrawer } =
    useFilters(DEFAULT_FILTER);

  const { sorting, setSorting, orderBy } = useDefaultSort({
    defaultSorting: [{ id: 'fullName', desc: false }],
  });

  const { userList, isFetching } = useUserList({
    skip,
    take,
    labelIds: labelId,
    ...(orderBy && { orderBy }),
    ...filterData,
    ...(searchTerm && { search: searchTerm }),
  });

  const onClick = (row: UserListItem) => {
    navigate(SETTINGS_ROUTES.USER_DETAIL(row.id));
  };

  useEffect(() => {
    if (!searchParams.size) {
      setSearchParams(new URLSearchParams(DEFAULT_FILTER));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run only once page loads

  return (
    <>
      {isSublabel ? (
        <HeaderSmall>
          {t('labelDetails.usersCount', {
            usersCount: total,
          })}
        </HeaderSmall>
      ) : null}
      <ListControls
        placeholder={t('user.search')}
        page={page}
        total={userList?.total ?? 0}
        perPage={perPage}
        entity="users"
        onFilterClick={openDrawer}
        setPageNumber={setPageNumber}
      />
      <Table
        data={userList?.data ?? userEmptyArray}
        columns={columns}
        rowClassName="odd:bg-level-03"
        onRowClick={onClick}
        sort={{
          sorting,
          onSortingChange: setSorting,
        }}
        skeletonLoading={{ isLoading: isFetching, prefillRowsNumber: 10 }}
      />
      <FilterDrawer open={drawerOpen} onOpenChange={closeDrawer}>
        <LabelUserFormFilter open={drawerOpen} onApply={closeDrawer} />
      </FilterDrawer>
    </>
  );
}

Users.displayName = 'LabelUsers';
